import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  FlagIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
  PencilSquareIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import PageLoader from '../../components/PageLoader';
import { EmptyState } from '../../ui';
import { TemperaturePill, DispositionBadge } from '../../components/intelligence';
import SendSMSModal, { type SmsLeadTarget } from '../../components/SendSMSModal';
import SendEmailModal, { type EmailLeadTarget } from '../../components/SendEmailModal';
import { useAuth } from '../../context/AuthContext';
import { useSmsEligibility } from '../../utils/entitlements';
import api, { followUpsAPI, callsAPI, extractErrorMessage, type FollowUpListItem } from '../../services/api';

function formatDueBy(fu: FollowUpListItem): string {
  if (fu.follow_up_deferred_until) {
    try {
      const d = new Date(fu.follow_up_deferred_until);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${dd}/${mm}/${yyyy}, ${hh}:${min}`;
    } catch {
      return fu.follow_up_deferred_until;
    }
  }
  const deadline = fu.follow_up_action?.deadline_mentioned;
  return deadline || '—';
}

function MetricTile({
  label,
  value,
  icon: Icon,
  color,
  bg,
}: {
  label: string;
  value: number;
  icon: React.ElementType;
  color: string;
  bg: string;
}) {
  return (
    <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-3 flex items-center gap-2.5">
      <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${bg}`}>
        <Icon className={`h-3.5 w-3.5 ${color}`} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-semibold text-white tabular-nums">{value.toLocaleString()}</p>
      </div>
    </div>
  );
}

const FollowUpsPage: React.FC = () => {
  const { user } = useAuth();
  const { canSms } = useSmsEligibility(user);
  // LT-92: email no longer gated by per-user SMTP. Backend uses platform SendGrid + plan check.
  const canSendEmail = canSms;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [followUps, setFollowUps] = useState<FollowUpListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [updatingCallSummaryId, setUpdatingCallSummaryId] = useState<string | null>(null);
  const [editingDeferredId, setEditingDeferredId] = useState<string | null>(null);
  const [deferredUntilLocal, setDeferredUntilLocal] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [smsLead, setSmsLead] = useState<SmsLeadTarget | null>(null);
  const [emailLead, setEmailLead] = useState<EmailLeadTarget | null>(null);
  const [smsToast, setSmsToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  /** Edit lead phone/email (same API as Leads page) */
  const [leadEditRow, setLeadEditRow] = useState<FollowUpListItem | null>(null);
  const [editLeadPhone, setEditLeadPhone] = useState('');
  const [editLeadEmail, setEditLeadEmail] = useState('');
  const [savingLeadContact, setSavingLeadContact] = useState(false);

  const openLeadContactEdit = (fu: FollowUpListItem) => {
    if (!fu.lead_id) return;
    setLeadEditRow(fu);
    setEditLeadPhone(fu.lead_phone || '');
    setEditLeadEmail((fu.lead_email || '').trim());
  };

  const closeLeadContactEdit = () => {
    setLeadEditRow(null);
    setEditLeadPhone('');
    setEditLeadEmail('');
  };

  const saveLeadContact = async () => {
    if (!leadEditRow?.lead_id) return;
    const phone = editLeadPhone.trim();
    if (!phone) {
      window.alert('Phone number is required.');
      return;
    }
    try {
      setSavingLeadContact(true);
      await api.put(`/leads/${leadEditRow.lead_id}`, {
        name: leadEditRow.lead_name,
        phone,
        email: editLeadEmail.trim() || null,
      });
      closeLeadContactEdit();
      fetchFollowUps();
      setSmsToast({ type: 'success', message: 'Lead contact updated.' });
    } catch (e: unknown) {
      setSmsToast({ type: 'error', message: extractErrorMessage(e, 'Failed to update lead.') });
    } finally {
      setSavingLeadContact(false);
    }
  };

  const fetchFollowUps = React.useCallback(() => {
    followUpsAPI
      .getFollowUps()
      .then((data) => setFollowUps(data.follow_ups ?? []))
      .catch(() => setError('Failed to load follow-ups.'));
  }, []);

  const statusCounts = useMemo(() => {
    let pending = 0;
    let completed = 0;
    let notRequired = 0;
    let deferred = 0;
    for (const fu of followUps) {
      const s = (fu.follow_up_status || '').toLowerCase();
      if (!s) pending += 1;
      else if (s === 'completed') completed += 1;
      else if (s === 'not_required') notRequired += 1;
      else if (s === 'deferred') deferred += 1;
      else pending += 1;
    }
    return { pending, completed, notRequired, deferred };
  }, [followUps]);

  const filteredFollowUps = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return followUps;
    const norm = (s: string) => s.replace(/\s|-|\(|\)/g, '').toLowerCase();
    return followUps.filter((fu) => {
      const nameMatch = fu.lead_name.toLowerCase().includes(term);
      const phoneNorm = norm(fu.lead_phone);
      const termNorm = norm(term);
      const phoneMatch = phoneNorm.includes(termNorm) || termNorm.includes(phoneNorm);
      const desc = (fu.follow_up_action?.description || fu.follow_up_action?.action_type || '').toLowerCase();
      const runName = (fu.run_name || '').toLowerCase();
      const emailMatch = (fu.lead_email || '').toLowerCase().includes(term);
      const freeTextMatch = desc.includes(term) || runName.includes(term);
      return nameMatch || phoneMatch || emailMatch || freeTextMatch;
    });
  }, [followUps, searchTerm]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    followUpsAPI
      .getFollowUps()
      .then((data) => {
        if (cancelled) return;
        setFollowUps(data.follow_ups ?? []);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load follow-ups.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleStatusChange = async (callSummaryId: string, status: 'pending' | 'completed' | 'not_required' | 'deferred', deferredUntil?: string) => {
    try {
      setUpdatingCallSummaryId(callSummaryId);
      await callsAPI.updateFollowUpStatus(callSummaryId, status, deferredUntil);
      setEditingDeferredId(null);
      fetchFollowUps();
    } catch {
      window.alert('Failed to update follow-up status. Please try again.');
    } finally {
      setUpdatingCallSummaryId(null);
    }
  };

  const openDeferredEditor = (callSummaryId: string) => {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 1);
    defaultDate.setMinutes(0, 0, 0);
    setDeferredUntilLocal(defaultDate.toISOString().slice(0, 16));
    setEditingDeferredId(callSummaryId);
  };

  if (loading) return <PageLoader message="Loading follow-ups..." />;

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-32 sm:-right-64 -top-32 sm:-top-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full" />
        <div className="absolute -left-32 sm:-left-64 -bottom-32 sm:-bottom-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full" />
      </div>

      <div className="relative ml-0 mr-auto w-full lg:w-[calc(100vw-14rem)] max-w-[1600px] px-4 lg:px-6 py-4 lg:py-5">
        <div className="animate-in slide-in-from-top duration-700 mb-4 sm:mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg shadow-lg">
                  <FlagIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-50">
                  Follow-ups
                </h1>
              </div>
              <p className="text-gray-400 text-xs sm:text-sm mt-1">
                View and manage all follow-ups across campaigns
              </p>
            </div>
            <div className="relative w-full sm:w-auto sm:min-w-[20rem] lg:min-w-[28rem] sm:max-w-2xl sm:flex-shrink-0">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <input
                type="search"
                placeholder="Search by lead name, phone, or free text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-600/60 bg-slate-800/60 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-400/50 bg-red-500/10 text-red-200 px-4 py-3">
            {error}
          </div>
        )}

        {/* Summary by status */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
          <MetricTile
            label="Pending"
            value={statusCounts.pending}
            icon={ClockIcon}
            color="text-amber-400"
            bg="bg-amber-500/20"
          />
          <MetricTile
            label="Completed"
            value={statusCounts.completed}
            icon={CheckCircleIcon}
            color="text-emerald-400"
            bg="bg-emerald-500/20"
          />
          <MetricTile
            label="Not required"
            value={statusCounts.notRequired}
            icon={XCircleIcon}
            color="text-slate-400"
            bg="bg-slate-500/20"
          />
          <MetricTile
            label="Deferred"
            value={statusCounts.deferred}
            icon={CalendarIcon}
            color="text-blue-400"
            bg="bg-blue-500/20"
          />
        </div>

        {/* Desktop table (lg and up) */}
        <div className="hidden lg:block">
          {followUps.length === 0 ? (
            // LT-314: DS-styled empty state. No CTA because follow-ups are
            // generated by the campaign runner, not created by the user.
            <EmptyState
              surface="followups_list"
              icon={FlagIcon}
              headline="No follow-ups"
              subhead="Follow-ups generated by your campaigns show up here — once a call leaves a contact in a state that needs human attention, we'll queue the task automatically."
              primary={{ label: 'Go to Campaigns', to: '/campaigns' }}
            />
          ) : (
            <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg sm:rounded-xl shadow-2xl border border-slate-700/50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-[16%]" />
                  <col className="w-[12%]" />
                  <col className="w-[14%]" />
                  <col className="w-[7%]" />
                  <col className="w-[11%]" />
                  <col className="w-[11%]" />
                  <col className="w-[12%]" />
                  <col className="w-[11%]" />
                  <col className="w-[9%]" />
                </colgroup>
                <thead className="bg-slate-800/30">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium text-slate-400 uppercase">Follow-up</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-slate-400 uppercase">Lead</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-slate-400 uppercase">Email</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-slate-400 uppercase">Temp</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-slate-400 uppercase">Disposition</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-slate-400 uppercase">Campaign</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-slate-400 uppercase">Due by</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/30">
                  {filteredFollowUps.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-2 py-8 text-center text-sm text-slate-400">
                        No follow-ups match your search.
                      </td>
                    </tr>
                  ) : (
                    filteredFollowUps.map((fu) => (
                      <tr
                        key={fu.call_summary_id}
                        className="hover:bg-slate-800/30 transition-colors"
                      >
                        <td className="px-2 py-2.5 text-sm text-slate-300">
                          <p className="line-clamp-2 break-words">
                            {fu.follow_up_action?.description || fu.follow_up_action?.action_type?.replace(/_/g, ' ') || '—'}
                          </p>
                          <button
                            type="button"
                            onClick={() => navigate(`/calls/${fu.call_summary_id}`)}
                            className="text-xs text-cyan-300 hover:text-cyan-200 underline underline-offset-2 mt-0.5"
                          >
                            View call details
                          </button>
                        </td>
                        <td className="px-2 py-2.5 text-sm">
                          <span className="font-medium text-white truncate block">{fu.lead_name}</span>
                          <span className="text-slate-400 block text-xs truncate">{fu.lead_phone}</span>
                        </td>
                        <td className="px-2 py-2.5 text-sm text-slate-300">
                          <span className="truncate block text-xs" title={(fu.lead_email || '').trim() || undefined}>
                            {(fu.lead_email || '').trim() || '—'}
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-sm">
                          <TemperaturePill temperature={fu.lead_temperature} />
                        </td>
                        <td className="px-2 py-2.5 text-sm">
                          <DispositionBadge disposition={fu.disposition} />
                        </td>
                        <td className="px-2 py-2.5 text-sm text-slate-300 truncate">{fu.run_name || '—'}</td>
                        <td className="px-2 py-2.5 text-sm" onClick={(e) => e.stopPropagation()}>
                          {updatingCallSummaryId === fu.call_summary_id ? (
                            <span className="text-slate-400">Updating...</span>
                          ) : editingDeferredId === fu.call_summary_id ? (
                            <div className="flex flex-col gap-1.5">
                              <input
                                type="datetime-local"
                                value={deferredUntilLocal}
                                onChange={(e) => setDeferredUntilLocal(e.target.value)}
                                className="rounded border border-slate-600 bg-slate-800 text-white text-xs px-2 py-1"
                              />
                              <div className="flex gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => handleStatusChange(fu.call_summary_id, 'deferred', new Date(deferredUntilLocal).toISOString())}
                                  className="px-2 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingDeferredId(null)}
                                  className="px-2 py-1 text-xs rounded bg-slate-600/50 text-slate-300 hover:bg-slate-600/70"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <select
                              value={fu.follow_up_status || 'pending'}
                              onChange={(e) => {
                                const v = e.target.value as 'pending' | 'completed' | 'not_required' | 'deferred';
                                if (v === 'deferred') openDeferredEditor(fu.call_summary_id);
                                else handleStatusChange(fu.call_summary_id, v);
                              }}
                              className="rounded border border-slate-600 bg-slate-800 text-white text-xs px-2 py-1 capitalize"
                            >
                              <option value="pending">Pending</option>
                              <option value="completed">Completed</option>
                              <option value="not_required">Not required</option>
                              <option value="deferred">Deferred</option>
                            </select>
                          )}
                        </td>
                        <td className="px-2 py-2.5 text-sm text-slate-300 whitespace-nowrap">{formatDueBy(fu)}</td>
                        <td className="px-2 py-2.5 text-sm">
                          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => openLeadContactEdit(fu)}
                              className={`p-1.5 rounded-md transition-colors ${fu.lead_id ? 'text-slate-400 hover:text-cyan-400 hover:bg-slate-700/50' : 'text-slate-600 cursor-not-allowed'}`}
                              title={fu.lead_id ? 'Edit lead phone & email' : 'No lead linked'}
                              disabled={!fu.lead_id}
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                            {canSms && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSmsLead({ id: fu.lead_id || '', name: fu.lead_name, phone: fu.lead_phone });
                                }}
                                className="p-1.5 rounded-md text-slate-400 hover:text-emerald-400 hover:bg-slate-700/50 transition-colors"
                                title="Send SMS"
                              >
                                <ChatBubbleLeftRightIcon className="h-4 w-4" />
                              </button>
                            )}
                            {(fu.lead_email || '').trim() && fu.lead_id && canSendEmail && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEmailLead({
                                    id: fu.lead_id || '',
                                    name: fu.lead_name,
                                    email: (fu.lead_email || '').trim(),
                                  });
                                }}
                                className="p-1.5 rounded-md text-slate-400 hover:text-sky-400 hover:bg-slate-700/50 transition-colors"
                                title="Send email"
                              >
                                <EnvelopeIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            </div>
          )}
        </div>

        {/* Mobile / tablet cards */}
        <div className="lg:hidden">
          {followUps.length === 0 ? (
            // LT-314: same DS empty state as desktop — unified messaging.
            <EmptyState
              surface="followups_list_mobile"
              icon={FlagIcon}
              headline="No follow-ups"
              subhead="Follow-ups generated by your campaigns show up here."
              primary={{ label: 'Go to Campaigns', to: '/campaigns' }}
            />
          ) : filteredFollowUps.length === 0 ? (
            <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg shadow-2xl border border-slate-700/50 px-4 py-6 text-center text-sm text-slate-400">
              No follow-ups match your search.
            </div>
          ) : (
            <div className="space-y-3">
              {filteredFollowUps.map((fu) => (
                <div
                  key={fu.call_summary_id}
                  className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg shadow-2xl border border-slate-700/50 px-4 py-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase">Follow-up</p>
                      <p className="text-sm font-medium text-white">
                        {fu.follow_up_action?.description || fu.follow_up_action?.action_type?.replace(/_/g, ' ') || '—'}
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate(`/calls/${fu.call_summary_id}`)}
                        className="mt-1 text-xs text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
                      >
                        View call details
                      </button>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-xs font-medium text-slate-300">{formatDueBy(fu)}</span>
                      <div className="flex items-center gap-1">
                        <TemperaturePill temperature={fu.lead_temperature} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase">Lead</p>
                      <p className="text-sm font-medium text-white">{fu.lead_name}</p>
                      <p className="text-xs text-slate-400">{fu.lead_phone}</p>
                      <p className="text-xs font-semibold text-slate-400 uppercase mt-1.5">Email</p>
                      <p className="text-xs text-slate-500 break-all">
                        {(fu.lead_email || '').trim() ? (fu.lead_email || '').trim() : '—'}
                      </p>
                    </div>
                    <div className="flex-1 flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2">
                        <DispositionBadge disposition={fu.disposition} />
                      </div>
                      <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2">
                        {updatingCallSummaryId === fu.call_summary_id ? (
                          <span className="text-xs text-slate-400">Updating...</span>
                        ) : editingDeferredId === fu.call_summary_id ? (
                          <div className="flex flex-col gap-1.5 items-end max-w-[200px]">
                            <input
                              type="datetime-local"
                              value={deferredUntilLocal}
                              onChange={(e) => setDeferredUntilLocal(e.target.value)}
                              className="rounded border border-slate-600 bg-slate-800 text-white text-xs px-2 py-1 w-full"
                            />
                            <div className="flex gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleStatusChange(fu.call_summary_id, 'deferred', new Date(deferredUntilLocal).toISOString())}
                                className="px-2 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingDeferredId(null)}
                                className="px-2 py-1 text-xs rounded bg-slate-600/50 text-slate-300 hover:bg-slate-600/70"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <select
                            value={fu.follow_up_status || 'pending'}
                            onChange={(e) => {
                              const v = e.target.value as 'pending' | 'completed' | 'not_required' | 'deferred';
                              if (v === 'deferred') openDeferredEditor(fu.call_summary_id);
                              else handleStatusChange(fu.call_summary_id, v);
                            }}
                            className="rounded border border-slate-600 bg-slate-800 text-white text-xs px-2 py-1 capitalize"
                          >
                            <option value="pending">Pending</option>
                            <option value="completed">Completed</option>
                            <option value="not_required">Not required</option>
                            <option value="deferred">Deferred</option>
                          </select>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => openLeadContactEdit(fu)}
                      disabled={!fu.lead_id}
                      className={`p-1.5 rounded-md transition-colors ${fu.lead_id ? 'text-slate-400 hover:text-cyan-400 hover:bg-slate-700/50' : 'text-slate-600 cursor-not-allowed'}`}
                      title={fu.lead_id ? 'Edit lead phone & email' : 'No lead linked'}
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    {canSms && (
                      <button
                        type="button"
                        onClick={() => setSmsLead({ id: fu.lead_id || '', name: fu.lead_name, phone: fu.lead_phone })}
                        className="p-1.5 rounded-md text-slate-400 hover:text-emerald-400 hover:bg-slate-700/50 transition-colors"
                        title="Send SMS"
                      >
                        <ChatBubbleLeftRightIcon className="h-4 w-4" />
                      </button>
                    )}
                    {(fu.lead_email || '').trim() && fu.lead_id && canSendEmail && (
                      <button
                        type="button"
                        onClick={() => {
                          setEmailLead({
                            id: fu.lead_id || '',
                            name: fu.lead_name,
                            email: (fu.lead_email || '').trim(),
                          });
                        }}
                        className="p-1.5 rounded-md text-slate-400 hover:text-sky-400 hover:bg-slate-700/50 transition-colors"
                        title="Send email"
                      >
                        <EnvelopeIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {smsToast &&
        createPortal(
          <div
            className="fixed left-1/2 top-20 z-[200] w-full max-w-lg -translate-x-1/2 px-4 pointer-events-none"
            role="status"
          >
            <div
              className={`pointer-events-auto mx-auto flex max-w-lg items-center justify-between rounded-lg p-3 shadow-lg ${
                smsToast.type === 'success' ? 'border border-green-500/30 bg-green-500/20' : 'border border-red-500/30 bg-red-500/20'
              }`}
            >
              <span className={`text-sm whitespace-pre-line ${smsToast.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>{smsToast.message}</span>
              <button type="button" onClick={() => setSmsToast(null)} className="ml-2 text-gray-400 hover:text-white">
                ×
              </button>
            </div>
          </div>,
          document.body
        )}

      {leadEditRow && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            aria-label="Close dialog"
            onClick={() => !savingLeadContact && closeLeadContactEdit()}
          />
          <div className="relative z-10 w-full max-w-md rounded-xl border border-slate-600/50 bg-slate-800 shadow-xl p-5">
            <h3 className="text-lg font-semibold text-white">Edit lead contact</h3>
            <p className="text-sm text-slate-400 mt-1">{leadEditRow.lead_name}</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Phone</label>
                <input
                  type="text"
                  value={editLeadPhone}
                  onChange={(e) => setEditLeadPhone(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/80 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                  placeholder="Phone number"
                  disabled={savingLeadContact}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Email</label>
                <input
                  type="email"
                  value={editLeadEmail}
                  onChange={(e) => setEditLeadEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/80 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                  placeholder="Email (optional)"
                  disabled={savingLeadContact}
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeLeadContactEdit}
                disabled={savingLeadContact}
                className="px-4 py-2 rounded-lg bg-slate-600/50 text-slate-200 text-sm hover:bg-slate-600/70 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void saveLeadContact()}
                disabled={savingLeadContact || !editLeadPhone.trim()}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-medium hover:from-cyan-600 hover:to-blue-700 disabled:opacity-50"
              >
                {savingLeadContact ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SendSMSModal
        isOpen={!!smsLead}
        onClose={() => setSmsLead(null)}
        lead={smsLead}
        setToast={setSmsToast}
      />
      <SendEmailModal
        isOpen={!!emailLead}
        onClose={() => setEmailLead(null)}
        lead={emailLead}
        setToast={setSmsToast}
      />
    </div>
  );
};

export default FollowUpsPage;
